import React, { useState, useEffect } from "react";
import { getFirestore, collection, getDocs, query, orderBy, doc, deleteDoc } from "firebase/firestore";
import Button from '@mui/material/Button';
import Container from '@mui/material/Container';
import IconButton from '@mui/material/IconButton';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import Snackbar from '@mui/material/Snackbar';
import MuiAlert from '@mui/material/Alert';
import { Typography, Grid, Box, Paper, Stack, Divider, TextField, List, ListItem, ListItemAvatar, ListItemText, ListItemButton, Avatar } from "@mui/material";
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import ShareIcon from '@mui/icons-material/Share';

const firestore = getFirestore();

const IMAGE_FIELDS = [
    { key: 'fotoCarnet', label: 'Foto Carnet' },
    { key: 'ciFrente', label: 'CI Frente' },
    { key: 'ciDorso', label: 'CI Dorso' },
    { key: 'registroFrente', label: 'Registro Frente' },
    { key: 'registroDorso', label: 'Registro Dorso' },
];

const Alert = React.forwardRef(function Alert(props, ref) {
    return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

function ListFuncionarios() {
    const [funcionarios, setFuncionarios] = useState([]);
    const [selectedFuncionario, setSelectedFuncionario] = useState(null);
    const [searchTerm, setSearchTerm] = useState("");
    const [openDeleteModal, setOpenDeleteModal] = useState(false);
    const [funcionarioToDelete, setFuncionarioToDelete] = useState(null);
    const [snackbarInfo, setSnackbarInfo] = useState({ text: '', type: '', open: false });

    useEffect(() => {
        const loadFuncionarios = async () => {
            try {
                const q = query(collection(firestore, "funcionarios"), orderBy("id", "asc"));
                const snapshot = await getDocs(q);
                const items = snapshot.docs.map(d => ({ docId: d.id, ...d.data() }));
                setFuncionarios(items);
                if (items.length > 0) {
                    setSelectedFuncionario(items[0]);
                }
            } catch (error) {
                console.error("Error al cargar funcionarios:", error);
            }
        };
        loadFuncionarios();
    }, []);

    const handleDeleteConfirm = async () => {
        try {
            await deleteDoc(doc(firestore, "funcionarios", String(funcionarioToDelete)));
            setFuncionarios(prev => {
                const nextList = prev.filter(f => f.docId !== String(funcionarioToDelete));
                if (selectedFuncionario && selectedFuncionario.docId === String(funcionarioToDelete)) {
                    setSelectedFuncionario(nextList.length > 0 ? nextList[0] : null);
                }
                return nextList;
            });
            setSnackbarInfo({ text: 'Funcionario eliminado correctamente!', type: 'success', open: true });
        } catch (error) {
            console.error("Error al eliminar:", error);
            setSnackbarInfo({ text: 'Ocurrió un error al eliminar', type: 'error', open: true });
        } finally {
            setOpenDeleteModal(false);
            setFuncionarioToDelete(null);
        }
    };

    const handleCloseSnackbar = (event, reason) => {
        if (reason === 'clickaway') return;
        setSnackbarInfo({ text: '', type: 'info', open: false });
    };

    const openMaps = (lat, lng) => {
        if (lat && lng) {
            window.open(`https://www.google.com/maps?q=${lat},${lng}`, '_blank');
        }
    };

    const getMainImage = (funcionario) => {
        return funcionario?.fotoCarnet || "/avatar_placeholder.jpg";
    };

    const filteredFuncionarios = funcionarios.filter(f => 
        (f.nombre || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
        (f.nroCedula || "").toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
        <Container component="main" maxWidth="xl" sx={{ mt: 3, mb: 4 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
                <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#333' }}>
                    Listado de Funcionarios
                </Typography>
                <Stack direction="row" spacing={1.5}>
                    <Button variant="contained" href="/add-funcionario" color="success">
                        Nuevo Funcionario
                    </Button>
                    <Button variant="outlined" href="/cuadrillas">
                        Volver a Cuadrillas
                    </Button>
                </Stack>
            </Box>

            <Grid container spacing={3}>
                {/* Left Column: Scrollable List of Officials */}
                <Grid item xs={12} md={4} lg={3.5}>
                    <Paper elevation={2} sx={{ p: 2, borderRadius: '8px', display: 'flex', flexDirection: 'column', height: 'calc(100vh - 200px)' }}>
                        <TextField
                            variant="outlined"
                            size="small"
                            placeholder="Buscar por nombre o cédula..."
                            fullWidth
                            value={searchTerm}
                            onChange={(e) => {
                                const term = e.target.value;
                                setSearchTerm(term);
                                const filtered = funcionarios.filter(f => 
                                    (f.nombre || "").toLowerCase().includes(term.toLowerCase()) ||
                                    (f.nroCedula || "").toLowerCase().includes(term.toLowerCase())
                                );
                                if (filtered.length > 0) {
                                    setSelectedFuncionario(filtered[0]);
                                } else {
                                    setSelectedFuncionario(null);
                                }
                            }}
                            sx={{ mb: 2 }}
                        />
                        <List sx={{ flexGrow: 1, overflowY: 'auto' }}>
                            {filteredFuncionarios.map((funcionario) => {
                                const isSelected = selectedFuncionario?.docId === funcionario.docId;
                                return (
                                    <ListItem key={funcionario.docId} disablePadding divider sx={{ mb: 0.5 }}>
                                        <ListItemButton
                                            selected={isSelected}
                                            onClick={() => setSelectedFuncionario(funcionario)}
                                            sx={{
                                                borderRadius: '6px',
                                                borderLeft: isSelected ? '4px solid #2e7d32' : '4px solid transparent',
                                                backgroundColor: isSelected ? '#f1f8e9' : 'transparent',
                                                '&.Mui-selected': {
                                                    backgroundColor: '#f1f8e9',
                                                    '&:hover': {
                                                        backgroundColor: '#e1ebd5',
                                                    }
                                                }
                                            }}
                                        >
                                            <ListItemAvatar>
                                                <Avatar 
                                                    src={getMainImage(funcionario)} 
                                                    alt={funcionario.nombre} 
                                                    sx={{ width: 40, height: 40 }}
                                                />
                                            </ListItemAvatar>
                                            <ListItemText
                                                primary={funcionario.nombre || 'Sin nombre'}
                                                secondary={funcionario.correo || 'Sin correo'}
                                                primaryTypographyProps={{
                                                    variant: 'subtitle2',
                                                    fontWeight: isSelected ? 'bold' : 'medium',
                                                    color: isSelected ? '#1b5e20' : '#333',
                                                    noWrap: true
                                                }}
                                                secondaryTypographyProps={{
                                                    variant: 'caption',
                                                    color: 'text.secondary',
                                                    noWrap: true
                                                }}
                                            />
                                        </ListItemButton>
                                    </ListItem>
                                );
                            })}
                            {filteredFuncionarios.length === 0 && (
                                <Typography variant="body2" color="text.secondary" align="center" sx={{ my: 4 }}>
                                    No se encontraron funcionarios
                                </Typography>
                            )}
                        </List>
                    </Paper>
                </Grid>

                {/* Right Column: Detailed View */}
                <Grid item xs={12} md={8} lg={8.5}>
                    {selectedFuncionario ? (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                            {/* Header Panel */}
                            <Paper elevation={2} sx={{ p: 2, borderRadius: '8px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
                                <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#2e7d32' }}>
                                    {selectedFuncionario.nombre}
                                </Typography>
                                <Stack direction="row" spacing={1.5}>
                                    <Button
                                        variant="outlined"
                                        color="error"
                                        startIcon={<DeleteIcon />}
                                        onClick={() => {
                                            setFuncionarioToDelete(selectedFuncionario.docId);
                                            setOpenDeleteModal(true);
                                        }}
                                    >
                                        Eliminar
                                    </Button>
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        startIcon={<EditIcon />}
                                        href={`/funcionario/${selectedFuncionario.docId}`}
                                    >
                                        Editar
                                    </Button>
                                </Stack>
                            </Paper>

                            <Grid container spacing={3}>
                                {/* Profile Picture / Card */}
                                <Grid item xs={12} md={5}>
                                    <Paper elevation={2} sx={{ p: 3, borderRadius: '8px', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                        <Box sx={{ width: '100%', paddingTop: '120%', position: 'relative', borderRadius: '8px', overflow: 'hidden', mb: 2, backgroundColor: '#f9f9f9', border: '1px solid #eaeaea' }}>
                                            <img
                                                src={getMainImage(selectedFuncionario)}
                                                alt={selectedFuncionario.nombre}
                                                style={{
                                                    position: 'absolute',
                                                    top: 0,
                                                    left: 0,
                                                    width: '100%',
                                                    height: '100%',
                                                    objectFit: 'cover'
                                                }}
                                            />
                                        </Box>
                                        
                                        <Typography variant="h6" align="center" sx={{ fontWeight: 'bold', mb: 0.5, color: '#333' }}>
                                            {selectedFuncionario.nombre}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 0.5 }}>
                                            {selectedFuncionario.correo || 'Sin correo'}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 2 }}>
                                            {selectedFuncionario.telefono || 'Sin teléfono'}
                                        </Typography>
                                        
                                        <Divider sx={{ width: '100%', my: 1.5 }} />
                                        
                                        <Stack direction="row" spacing={2} justifyContent="space-between" alignItems="center" sx={{ width: '100%' }}>
                                            <Box>
                                                <Button 
                                                    size="small" 
                                                    color="error" 
                                                    onClick={() => {
                                                        setFuncionarioToDelete(selectedFuncionario.docId);
                                                        setOpenDeleteModal(true);
                                                    }}
                                                    sx={{ fontWeight: 'bold' }}
                                                >
                                                    ELIMINAR
                                                </Button>
                                                <Button 
                                                    size="small" 
                                                    href={`/funcionario/${selectedFuncionario.docId}`}
                                                    sx={{ fontWeight: 'bold', ml: 1 }}
                                                >
                                                    EDITAR
                                                </Button>
                                            </Box>
                                            <Stack direction="row" spacing={1}>
                                                <IconButton size="small">
                                                    <FavoriteBorderIcon fontSize="small" />
                                                </IconButton>
                                                <IconButton size="small">
                                                    <ShareIcon fontSize="small" />
                                                </IconButton>
                                            </Stack>
                                        </Stack>
                                    </Paper>
                                </Grid>

                                {/* Technical Details */}
                                <Grid item xs={12} md={7}>
                                    <Paper elevation={2} sx={{ p: 3, borderRadius: '8px', height: '100%' }}>
                                        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 2.5, color: '#2e7d32' }}>
                                            Detalles del Funcionario
                                        </Typography>
                                        
                                        <Grid container spacing={2.5}>
                                            <Grid item xs={12} sm={6}>
                                                <Typography variant="caption" color="text.secondary" display="block">
                                                    Nombre completo
                                                </Typography>
                                                <Typography variant="body2" sx={{ fontWeight: 'medium', color: '#333' }}>
                                                    {selectedFuncionario.nombre || '-'}
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={12} sm={6}>
                                                <Typography variant="caption" color="text.secondary" display="block">
                                                    Cédula de Identidad (C.I.)
                                                </Typography>
                                                <Typography variant="body2" sx={{ fontWeight: 'medium', color: '#333' }}>
                                                    {selectedFuncionario.nroCedula || '-'}
                                                </Typography>
                                            </Grid>
                                            
                                            <Grid item xs={12} sm={6}>
                                                <Typography variant="caption" color="text.secondary" display="block">
                                                    Correo electrónico
                                                </Typography>
                                                <Typography variant="body2" sx={{ fontWeight: 'medium', color: '#333', wordBreak: 'break-all' }}>
                                                    {selectedFuncionario.correo || '-'}
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={12} sm={6}>
                                                <Typography variant="caption" color="text.secondary" display="block">
                                                    Ciudad
                                                </Typography>
                                                <Typography variant="body2" sx={{ fontWeight: 'medium', color: '#333' }}>
                                                    {selectedFuncionario.ciudad || '-'}
                                                </Typography>
                                            </Grid>
                                            
                                            <Grid item xs={12} sm={6}>
                                                <Typography variant="caption" color="text.secondary" display="block">
                                                    Grupo Sanguíneo
                                                </Typography>
                                                <Typography variant="body2" sx={{ fontWeight: 'medium', color: '#333' }}>
                                                    {selectedFuncionario.grupoSanguineo || '-'}
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={12} sm={6}>
                                                <Typography variant="caption" color="text.secondary" display="block">
                                                    Teléfono
                                                </Typography>
                                                <Typography variant="body2" sx={{ fontWeight: 'medium', color: '#333' }}>
                                                    {selectedFuncionario.telefono || '-'}
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={12} sm={6}>
                                                <Typography variant="caption" color="text.secondary" display="block">
                                                    Seguro Kemi
                                                </Typography>
                                                <Typography variant="body2" sx={{ fontWeight: 'medium', color: '#333' }}>
                                                    {selectedFuncionario.seguroKemi || '-'}
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={12} sm={6}>
                                                <Typography variant="caption" color="text.secondary" display="block">
                                                    Seguro Sancor
                                                </Typography>
                                                <Typography variant="body2" sx={{ fontWeight: 'medium', color: '#333' }}>
                                                    {selectedFuncionario.seguroSancor || '-'}
                                                </Typography>
                                            </Grid>

                                            <Grid item xs={12} sm={6}>
                                                <Typography variant="caption" color="text.secondary" display="block">
                                                    Nombre del contacto
                                                </Typography>
                                                <Typography variant="body2" sx={{ fontWeight: 'medium', color: '#333' }}>
                                                    {selectedFuncionario.nombreContacto || '-'}
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={12} sm={6}>
                                                <Typography variant="caption" color="text.secondary" display="block">
                                                    Teléfono del contacto
                                                </Typography>
                                                <Typography variant="body2" sx={{ fontWeight: 'medium', color: '#333' }}>
                                                    {selectedFuncionario.contactoEmergencia || '-'}
                                                </Typography>
                                            </Grid>

                                            <Grid item xs={12}>
                                                <Typography variant="caption" color="text.secondary" display="block">
                                                    Coordenadas / Ubicación
                                                </Typography>
                                                {selectedFuncionario.latitud && selectedFuncionario.longitud ? (
                                                    <Stack direction="row" alignItems="center" spacing={1}>
                                                        <Typography variant="body2" sx={{ fontWeight: 'medium', color: '#333' }}>
                                                            Lat: {selectedFuncionario.latitud}, Lng: {selectedFuncionario.longitud}
                                                        </Typography>
                                                        <IconButton 
                                                            size="small" 
                                                            color="primary"
                                                            onClick={() => openMaps(selectedFuncionario.latitud, selectedFuncionario.longitud)}
                                                            title="Ver en Google Maps"
                                                        >
                                                            <LocationOnIcon fontSize="small" />
                                                        </IconButton>
                                                    </Stack>
                                                ) : (
                                                    <Typography variant="body2" color="text.secondary">-</Typography>
                                                )}
                                            </Grid>
                                        </Grid>
                                        
                                        <Divider sx={{ my: 2.5 }} />
                                        
                                        <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1.5, color: '#333' }}>
                                            Documentos Adjuntos
                                        </Typography>
                                        <Grid container spacing={1.5}>
                                            {IMAGE_FIELDS.map(({ key, label }) => {
                                                const url = selectedFuncionario[key];
                                                return (
                                                    <Grid item xs={6} sm={3} key={key}>
                                                        <Typography variant="caption" color="text.secondary" display="block" noWrap sx={{ mb: 0.5 }}>
                                                            {label}
                                                        </Typography>
                                                        {url ? (
                                                            <Box 
                                                                sx={{ 
                                                                    width: '100%', 
                                                                    paddingTop: '75%', 
                                                                    position: 'relative', 
                                                                    border: '1px solid #ccc', 
                                                                    borderRadius: '4px', 
                                                                    overflow: 'hidden',
                                                                    cursor: 'pointer',
                                                                    transition: 'opacity 0.2s',
                                                                    '&:hover': { opacity: 0.8 }
                                                                }}
                                                                onClick={() => window.open(url, '_blank')}
                                                                title="Ver en pestaña nueva"
                                                            >
                                                                <img 
                                                                    src={url} 
                                                                    alt={label} 
                                                                    style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', objectFit: 'cover' }} 
                                                                />
                                                            </Box>
                                                        ) : (
                                                            <Typography variant="caption" color="text.secondary" display="block" sx={{ py: 1, backgroundColor: '#f9f9f9', textAlign: 'center', border: '1px dashed #ccc', borderRadius: '4px' }}>
                                                                Sin imagen
                                                            </Typography>
                                                        )}
                                                    </Grid>
                                                );
                                            })}
                                        </Grid>
                                    </Paper>
                                </Grid>
                            </Grid>
                        </Box>
                    ) : (
                        <Paper elevation={2} sx={{ p: 4, borderRadius: '8px', display: 'flex', justifyContent: 'center', alignItems: 'center', height: 'calc(100vh - 200px)' }}>
                            <Typography variant="body1" color="text.secondary">
                                Seleccione un funcionario de la lista para ver los detalles.
                            </Typography>
                        </Paper>
                    )}
                </Grid>
            </Grid>

            {/* Delete Confirmation Modal */}
            <Dialog open={openDeleteModal} onClose={() => setOpenDeleteModal(false)}>
                <DialogTitle>¿Desea eliminar este funcionario?</DialogTitle>
                <DialogContent>
                    <DialogContentText>Los cambios serán permanentes.</DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenDeleteModal(false)}>Cerrar</Button>
                    <Button onClick={handleDeleteConfirm} autoFocus color="error">Aceptar</Button>
                </DialogActions>
            </Dialog>

            {/* Notification Snackbar */}
            <Snackbar open={snackbarInfo.open} autoHideDuration={2000} onClose={handleCloseSnackbar} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
                <Alert onClose={handleCloseSnackbar} severity={snackbarInfo.type} sx={{ width: '100%' }}>
                    {snackbarInfo.text}
                </Alert>
            </Snackbar>
        </Container>
    );
}

export default ListFuncionarios;
